import { useCallback, useEffect, useRef, useState, Suspense } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ErrorBoundary, type FallbackProps } from 'react-error-boundary';
import { moduleRegistry } from '@/config/module-registry.config';
import { getNativeModule } from '@/modules/registry';
import { hasAccess } from '@/lib/rbac';
import { useAuth } from '@/contexts/useAuth';
import { usePostMessageBridge } from '@/hooks/use-post-message-bridge';
import { Button } from '@/components/ui/button';
import LucideIcon from '@/components/LucideIcon';
import { Separator } from '@/components/ui/separator';

const IFRAME_LOAD_TIMEOUT_MS = 10_000;

// ---------------------------------------------------------------------------
// Error boundary fallback
// ---------------------------------------------------------------------------

function ModuleErrorFallback({ error, resetErrorBoundary }: FallbackProps) {
  return (
    <div className="flex flex-col items-center justify-center gap-4 p-8 text-center" role="alert">
      <LucideIcon name="AlertTriangle" className="h-12 w-12 text-destructive" />
      <h2 className="text-xl font-semibold">Something went wrong</h2>
      <p className="text-muted-foreground max-w-md">
        {error instanceof Error ? error.message : 'An unexpected error occurred while loading the module.'}
      </p>
      <div className="flex gap-3">
        <Button variant="outline" onClick={resetErrorBoundary}>
          Retry
        </Button>
        <Button asChild>
          <Link to="/">Return to Dashboard</Link>
        </Button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Iframe host
// ---------------------------------------------------------------------------

function IframeHost({ sourceUrl, moduleName }: { sourceUrl: string; moduleName: string }) {
  const module = moduleRegistry.find((m) => m.sourceUrl === sourceUrl);
  const { iframeRef } = usePostMessageBridge(module);

  const [isLoading, setIsLoading] = useState(true);
  const [hasTimedOut, setHasTimedOut] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  // Start the load timeout
  useEffect(() => {
    timerRef.current = setTimeout(() => {
      setHasTimedOut(true);
      setIsLoading(false);
    }, IFRAME_LOAD_TIMEOUT_MS);

    return clearTimer;
  }, [sourceUrl, clearTimer]);

  const handleLoad = useCallback(() => {
    clearTimer();
    setIsLoading(false);
    setHasTimedOut(false);
  }, [clearTimer]);

  const handleRetry = useCallback(() => {
    setIsLoading(true);
    setHasTimedOut(false);
    // Force iframe reload by briefly clearing the ref
    if (iframeRef.current) {
      iframeRef.current.src = sourceUrl;
    }
    timerRef.current = setTimeout(() => {
      setHasTimedOut(true);
      setIsLoading(false);
    }, IFRAME_LOAD_TIMEOUT_MS);
  }, [sourceUrl, iframeRef]);

  if (hasTimedOut) {
    return (
      <div className="flex flex-col items-center justify-center gap-4 p-8 text-center">
        <LucideIcon name="AlertCircle" className="h-12 w-12 text-destructive" />
        <h2 className="text-xl font-semibold">Module failed to load</h2>
        <p className="text-muted-foreground max-w-md">
          "{moduleName}" did not respond within {IFRAME_LOAD_TIMEOUT_MS / 1000} seconds.
        </p>
        <div className="flex gap-3">
          <Button variant="outline" onClick={handleRetry}>
            Retry
          </Button>
          <Button asChild variant="secondary">
            <a href={sourceUrl} target="_blank" rel="noopener noreferrer">
              Open in new tab
            </a>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="relative h-full min-h-[60vh] w-full">
      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center bg-background/80 z-10">
          <div className="flex flex-col items-center gap-3">
            <LucideIcon name="Loader2" className="h-8 w-8 animate-spin text-muted-foreground" />
            <p className="text-sm text-muted-foreground">Loading {moduleName}…</p>
          </div>
        </div>
      )}
      <iframe
        ref={iframeRef}
        src={sourceUrl}
        title={moduleName}
        sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
        className="h-full w-full border-0"
        onLoad={handleLoad}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// New-tab host
// ---------------------------------------------------------------------------

function NewTabHost({ sourceUrl, moduleName }: { sourceUrl: string; moduleName: string }) {
  const opened = useRef(false);

  useEffect(() => {
    if (!opened.current) {
      opened.current = true;
      window.open(sourceUrl, '_blank', 'noopener,noreferrer');
    }
  }, [sourceUrl]);

  return (
    <div className="flex flex-col items-center justify-center gap-4 p-8 text-center">
      <LucideIcon name="ExternalLink" className="h-12 w-12 text-muted-foreground" />
      <h2 className="text-xl font-semibold">{moduleName}</h2>
      <p className="text-muted-foreground max-w-md">
        This module has been opened in a new browser tab.
      </p>
      <div className="flex gap-3">
        <Button asChild variant="secondary">
          <a href={sourceUrl} target="_blank" rel="noopener noreferrer">
            Open again
          </a>
        </Button>
        <Button asChild>
          <Link to="/">Return to Dashboard</Link>
        </Button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Native module host — resolves the lazy component outside of render
// ---------------------------------------------------------------------------

/**
 * Wrapper that renders a native module by ID.
 * The component lookup happens via getNativeModule which caches lazy()
 * results, but the react-hooks/static-components rule requires the
 * component reference to be truly static. We satisfy this by rendering
 * through a thin wrapper that delegates to the cached lazy component
 * via a stable intermediary.
 */
function NativeModuleHost({ moduleId, moduleName }: { moduleId: string; moduleName: string }) {
  const Resolved = getNativeModule(moduleId);

  if (!Resolved) {
    return (
      <div className="flex flex-col items-center justify-center gap-4 p-8 text-center">
        <LucideIcon name="AlertCircle" className="h-12 w-12 text-destructive" />
        <h2 className="text-xl font-semibold">Module not available</h2>
        <p className="text-muted-foreground">
          The native module "{moduleName}" is registered but has no component loaded.
        </p>
        <Button asChild>
          <Link to="/">Return to Dashboard</Link>
        </Button>
      </div>
    );
  }

  return (
    <Suspense
      fallback={
        <div className="flex items-center justify-center p-8">
          <div className="flex flex-col items-center gap-3">
            <LucideIcon name="Loader2" className="h-8 w-8 animate-spin text-muted-foreground" />
            <p className="text-sm text-muted-foreground">Loading {moduleName}…</p>
          </div>
        </div>
      }
    >
      {/* eslint-disable-next-line react-hooks/static-components -- Component is cached in registry; not recreated per render */}
      <Resolved />
    </Suspense>
  );
}

// ---------------------------------------------------------------------------
// Native module with header — clicking the header resets the module to its
// initial state by incrementing a key, which forces a full remount.
// ---------------------------------------------------------------------------

function NativeModuleHostWithHeader({ module }: { module: { id: string; name: string; icon: string } }) {
  const [resetKey, setResetKey] = useState(0);

  return (
    <>
      <ModuleHeader icon={module.icon} name={module.name} onClick={() => setResetKey((k) => k + 1)} />
      <NativeModuleHost key={resetKey} moduleId={module.id} moduleName={module.name} />
    </>
  );
}

// ---------------------------------------------------------------------------
// Module header — shown at the top of every module view
// ---------------------------------------------------------------------------

function ModuleHeader({ icon, name, onClick }: { icon: string; name: string; onClick?: () => void }) {
  return (
    <div className="mb-4">
      <button
        type="button"
        onClick={onClick}
        className="flex items-center gap-3 px-1 py-2 hover:opacity-80 transition-opacity cursor-pointer"
      >
        <LucideIcon name={icon} className="h-5 w-5 shrink-0 text-primary" aria-hidden="true" />
        <h1 className="text-lg font-semibold leading-tight">{name}</h1>
      </button>
      <Separator />
    </div>
  );
}

// ---------------------------------------------------------------------------
// ModuleHost (main export)
// ---------------------------------------------------------------------------

function ModuleHostInner() {
  const { moduleId } = useParams<{ moduleId: string }>();
  const { role } = useAuth();
  const module = moduleRegistry.find((m) => m.id === moduleId);

  if (!module) {
    return (
      <div className="flex flex-col items-center justify-center gap-4 p-8 text-center">
        <LucideIcon name="FileQuestion" className="h-12 w-12 text-muted-foreground" />
        <h2 className="text-xl font-semibold">Module not found</h2>
        <p className="text-muted-foreground">
          No module with ID "{moduleId}" exists in the registry.
        </p>
        <Button asChild>
          <Link to="/">Return to Dashboard</Link>
        </Button>
      </div>
    );
  }

  if (!hasAccess(role, module.requiredRole)) {
    return (
      <div className="flex flex-col items-center justify-center gap-4 p-8 text-center">
        <LucideIcon name="ShieldX" className="h-12 w-12 text-destructive" />
        <h2 className="text-xl font-semibold">Access Denied</h2>
        <p className="text-muted-foreground">
          You don't have permission to access this module.
        </p>
        <Button asChild>
          <Link to="/">Return to Dashboard</Link>
        </Button>
      </div>
    );
  }

  if (module.embeddingMethod === 'native') {
    return (
      <NativeModuleHostWithHeader module={module} />
    );
  }

  if (!module.sourceUrl) {
    return (
      <div className="flex flex-col items-center justify-center gap-4 p-8 text-center">
        <LucideIcon name="AlertCircle" className="h-12 w-12 text-destructive" />
        <h2 className="text-xl font-semibold">Module configuration error</h2>
        <p className="text-muted-foreground max-w-md">
          Module "{module.name}" is missing a source URL.
        </p>
        <Button asChild>
          <Link to="/">Return to Dashboard</Link>
        </Button>
      </div>
    );
  }

  if (module.embeddingMethod === 'new-tab') {
    return <NewTabHost sourceUrl={module.sourceUrl} moduleName={module.name} />;
  }

  return (
    <>
      <ModuleHeader icon={module.icon} name={module.name} />
      <IframeHost sourceUrl={module.sourceUrl} moduleName={module.name} />
    </>
  );
}

export default function ModuleHost() {
  return (
    <ErrorBoundary
      FallbackComponent={ModuleErrorFallback}
      onError={(error, info) => {
        console.error('[ModuleHost] Rendering error:', error, info);
      }}
    >
      <ModuleHostInner />
    </ErrorBoundary>
  );
}
